<template>
  <div class="interpretation-container">
    <h1 class="hexagram-title">
      {{ mainChar }}
      <span v-if="hasChanged">(지괘 {{ changedChar }})</span>
    </h1>

    <!-- interpretation이 null일 때만 waiting-interpretation 표시 -->
    <div class="waiting-interpretation" v-if="!interpretation">
      <p v-if="hexagramInfo">
        <TypingEffect
          :text="hexagramInfoNameText"
          @typing-complete="handleTypingComplete('name')"
        />
        <br v-if="hexagramInfoNameText && hexagramInfoOverviewText" />
        <TypingEffect
          v-if="typingStatus.name"
          :text="hexagramInfoOverviewText"
          @typing-complete="handleTypingComplete('overview')"
        />
      </p>
      <p v-if="hexagramInfoForChanged && hasChanged">
        <TypingEffect
          v-if="typingStatus.overview"
          :text="hexagramInfoChangedNameText"
          @typing-complete="handleTypingComplete('changedName')"
        />
        <br
          v-if="hexagramInfoChangedNameText && hexagramInfoChangedOverviewText"
        />
        <TypingEffect
          v-if="typingStatus.changedName"
          :text="hexagramInfoChangedOverviewText"
          @typing-complete="handleTypingComplete('changedOverview')"
        />
      </p>
      <p v-if="typingStatus.allComplete && !apiResult" class="blinking-text">
        곧 해석 결과가 도착합니다. 잠시 기다려주세요.
      </p>
    </div>

    <div class="interpretation-content" v-if="interpretation">
      <p class="interpretation-text">{{ interpretation }}</p>
    </div>

    <!-- 썸네일 항상 표시 -->
    <div v-if="interpretation" style="text-align: center; margin-top: 1.5rem">
      <img
        :src="`${downloadBaseURL}/getThumb/${encodeURIComponent(fullName)}`"
        alt="썸네일"
        style="max-width: 80%; margin-bottom: 0.5rem"
      />

      <!-- 괘 다운로드 버튼 (조건부 노출) -->
      <div class="download-link-wrapper">
        <a
          v-if="canDownload"
          @click.prevent="downloadGua"
          class="download-link"
        >
          괘 다운로드({{ downloadPrice }}LYD)
        </a>
      </div>
    </div>
    <div class="button-area">
      <!--button @click="onShare">공유</button-->
      <!--button @click="onSave">저장</button-->
      <button @click="onExit">돌아가기</button>
    </div>
  </div>
  <div v-if="isDownloading" class="spinner-wrapper">
    <div v-if="!downloadComplete" class="spinner"></div>
    <div v-else class="download-complete-message">{{ downloadResult }}</div>
  </div>
</template>

<script setup>
  import { onMounted, ref, computed, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import TypingEffect from '@/components/TypingEffect.vue' // TypingEffect 컴포넌트 임포트
  import { hexagramNames } from '@/data/hexagramMap'
  import { hexagramOverview } from '@/data/hexagramOverview'
  import { GraphQLClient, gql } from 'graphql-request' // graphql-request import
  import { useStore } from 'vuex'
  import { canUseThis, deductLydiaForMenu } from '@/utils/checkLydia' // 권한 확인 유틸리티 함수 임포트

  const router = useRouter()
  const route = useRoute()
  const store = useStore()
  const mainKey = route.query.main
  const changedKey = route.query.changed
  const question = route.query.question || ''
  const effString = route.query.changedEffs || ''
  const birth = route.query.birth || ''

  const changedEffs = effString.split(',').map((e) => e.trim())

  const mainChar = computed(() => {
    const hexagramName = hexagramNames[mainKey]
    if (hexagramName) {
      console.log('본괘: ' + hexagramName)
      return hexagramName.split('(')[0]
    }
    return ''
  })

  const changedChar = computed(() => {
    const hexagramName = hexagramNames[changedKey]
    if (hexagramName) {
      console.log('지괘: ' + hexagramName)
      return hexagramName.split('(')[0]
    }
    return ''
  })

  const hasChanged = computed(
    () => changedChar.value && changedChar.value !== mainChar.value,
  )

  const hexagramInfo = computed(() => {
    return hexagramOverview.find((item) =>
      item['괘이름'].includes(mainChar.value),
    )
  })
  //한자이름을 가지고 검색해야 한다.
  const hexagramInfoForChanged = computed(() => {
    return hexagramOverview.find((item) =>
      item['괘이름'].includes(changedChar.value),
    )
  })
  //
  const interpretation = ref(null)
  const apiResult = ref(null)

  const effLabels = ['초효', '2효', '3효', '4효', '5효', '상효']
  const effStatus = changedEffs
    .map(
      (eff, i) =>
        `${effLabels[i]}: ${eff}${eff.includes('노') ? ' ← 변효' : ''}`,
    )
    .join('\n')

  // 타이핑 완료 상태를 추적하기 위한 변수
  const typingStatus = ref({
    name: false,
    overview: false,
    changedName: false,
    changedOverview: false,
    allComplete: false,
  })

  // TypingEffect 컴포넌트에 전달할 텍스트
  const hexagramInfoNameText = computed(() =>
    hexagramInfo.value ? `${hexagramInfo.value.괘이름} 괘를 얻으셨습니다.` : '',
  )
  const hexagramInfoOverviewText = computed(() =>
    hexagramInfo.value
      ? `상괘는 ${hexagramInfo.value.상괘}이고, 하괘는 ${hexagramInfo.value.하괘}입니다.\n${hexagramInfo.value.형상풀이}\n${hexagramInfo.value.의미}`
      : '',
  )
  const hexagramInfoChangedNameText = computed(() =>
    hexagramInfoForChanged.value && hasChanged.value
      ? `지괘로 ${hexagramInfoForChanged.value.괘이름} 괘를 얻으셨습니다.`
      : '',
  )
  const hexagramInfoChangedOverviewText = computed(() =>
    hexagramInfoForChanged.value && hasChanged.value
      ? `상괘는 ${hexagramInfoForChanged.value.상괘}이고, 하괘는 ${hexagramInfoForChanged.value.하괘}입니다.\n${hexagramInfoForChanged.value.형상풀이}\n${hexagramInfoForChanged.value.의미}`
      : '',
  )

  const isDownloading = ref(false)
  const downloadComplete = ref(false) // 다운로드 완료 상태 변수 추가
  const downloadResult = ref('') // 다운로드 결과 메시지 변수

  const fullName = computed(() => hexagramInfo.value?.전체괘명 || '')
  const downloadPrice = parseInt(import.meta.env.VITE_CHARGE_DOWNLOAD_GUA)
  const canDownload = computed(() => {
    return store.getters.getLydia >= downloadPrice
  })

  const downloadBaseURL = import.meta.env.VITE_DOWNLOAD_ENDPOINT
  const downloadGua = async () => {
    if (isDownloading.value) return // 이미 다운로드 중이면 중복 실행 방지

    isDownloading.value = true // 다운로드 시작 시 로딩 상태 true
    downloadComplete.value = false // 다운로드 완료 상태 초기화

    const ok = await canUseThis('downloadGua', store)
    if (!ok) {
      isDownloading.value = false // 잔액 부족 시 로딩 상태 false
      return alert('다운로드를 위한 잔액이 부족하거나 오류가 발생했습니다.')
    }

    try {
      const response = await fetch(
        `${downloadBaseURL}/getGua/${encodeURIComponent(fullName.value)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken: localStorage.getItem('accessToken'),
          }),
        },
      )

      if (!response.ok) {
        throw new Error('서버 응답 오류')
      }

      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${fullName.value}.png`
      a.click()
      URL.revokeObjectURL(url)

      downloadResult.value = '다운로드가 완료되었습니다.'

      await deductLydiaForMenu('downloadGua', store) // Lydia 차감

      downloadComplete.value = true
      setTimeout(() => {
        isDownloading.value = false
        downloadComplete.value = false
      }, 1000) // 텍스트 2초간 보여주기
    } catch (err) {
      downloadResult.value = `다운로드 중 오류가 발생했습니다.(${err.message})`
      downloadComplete.value = true
      setTimeout(() => {
        isDownloading.value = false
        downloadComplete.value = false
      }, 1000) // 텍스트 1초간 보여주기
      //await store.dispatch('refundLydia', 'downloadGua') // 다운로드 실패 시 충전 금액 환불
    }
  }

  // TypingEffect 컴포넌트에서 타이핑 완료 이벤트를 받을 핸들러
  const handleTypingComplete = (section) => {
    typingStatus.value[section] = true

    // 모든 타이핑이 완료되었는지 확인
    let allTypingDone = typingStatus.value.name && typingStatus.value.overview
    if (hasChanged.value) {
      allTypingDone =
        allTypingDone &&
        typingStatus.value.changedName &&
        typingStatus.value.changedOverview
    }

    if (allTypingDone) {
      typingStatus.value.allComplete = true
      // 모든 타이핑 완료 후 API 결과가 있으면 interpretation에 할당
      if (apiResult.value != null) {
        interpretation.value = apiResult.value
      }
    }
  }

  const client = new GraphQLClient(import.meta.env.VITE_API_ENDPOINT)
  const GET_DIVINATION_RESULT_MUTATION = gql`
    mutation getDivinationResult($input: DivinationInput!) {
      getDivinationResult(input: $input) {
        interpretation
      }
    }
  `

  const fetchInterpretation = async () => {
    const variables = {
      input: {
        mainChar: mainChar.value,
        changedChar: changedChar.value,
        changedEffs: changedEffs,
        question: question,
        birth: birth,
      },
    }

    try {
      const data = await client.request(
        GET_DIVINATION_RESULT_MUTATION,
        variables,
      )
      apiResult.value = data.getDivinationResult.interpretation

      await deductLydiaForMenu('divination', store) // Lydia 차감

      // API 결과 도착 후 모든 타이핑이 완료되었으면 interpretation에 할당
      if (typingStatus.value.allComplete) {
        interpretation.value = apiResult.value
      }
    } catch (error) {
      console.error('Error fetching divination result:', error)
      apiResult.value = '점괘 해석을 가져오는 중 오류가 발생했습니다.'

      // 오류 발생 시에도 모든 타이핑이 완료되었으면 interpretation에 오류 메시지 할당
      if (typingStatus.value.allComplete) {
        interpretation.value = apiResult.value
      }
    }
  }

  onMounted(() => {
    fetchInterpretation() // 컴포넌트 마운트 시 API 호출 시작
  })

  function onShare() {
    // 공유 기능 연결 예정
  }

  function onSave() {
    // 저장 기능 연결 예정
  }

  function onExit() {
    router.go(-1)
  }
</script>

<style scoped>
  .interpretation-container {
    padding: 1.0rem;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 100vh;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    box-sizing: border-box;
  }

  .hexagram-title {
    font-size: var(--font-size-14);
    text-align: center;
    margin-top: 0.5rem;
    font-weight: bold;
  }

  .waiting-interpretation {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0.5rem;
    font-size: var(--font-size-10);
    box-sizing: border-box;
    align-self: center;
    width: 90%;
  }

  .waiting-interpretation p {
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 1.6;
    text-align: justify;
  }

  .blinking-text {
    animation: blink 2s infinite;
    color: #888;
    margin-top: 1.2rem;
    text-align: center;
  }
  @keyframes blink {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  .interpretation-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    font-size: var(--font-size-10);
    box-sizing: border-box;
    align-self: center;
    width: 90%;
  }

  .interpretation-text {
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 1.6;
    text-align: justify;
  }

  .download-area {
    text-align: center;
    margin-top: 2rem;
  }

  .gua-thumbnail {
    width: 180px;
    height: auto;
    margin-bottom: 1rem;
  }

  .download-link-wrapper {
    margin-bottom: 2.5rem; /* 돌아가기 버튼과 간격 확보 */
  }

  .download-link {
    color: #1a73e8;
    font-weight: bold;
    cursor: pointer;
  }
  .download-link:hover {
    text-decoration: underline;
  }

  .button-area {
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
  }

  button {
    padding: 0.7rem 1.5rem;
    font-size: var(--font-size-10);
    border: none;
    border-radius: 8px;
    background-color: #f44336;
    color: white;
    cursor: pointer;
  }

  button:hover {
    background-color: #da190b;
  }

  .spinner-wrapper {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: var(--device-width);
    max-width: 480px;
    margin: auto;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  .spinner {
    border: 6px solid #ccc;
    border-top: 6px solid #4caf50;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .download-complete-message {
    font-size: var(--font-size-12);
    color: white;
  }
</style>
